import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BedDouble,
  Users,
  ClipboardList,
  CheckCircle,
  Clock,
  ArrowRight,
  TrendingUp,
  LogIn,
  LogOut
} from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';
import StatCard from '../../components/ui/StatCard';
import { useAuth } from '../../context/AuthContext';
import { adminsAPI, bookingsAPI, stationsAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const AdminDashboard = () => {
  const { admin } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [station, setStation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStation, setSelectedStation] = useState('');
  const [stations, setStations] = useState([]);

  useEffect(() => {
    if (admin?.role === 'super_admin') {
      fetchStations();
    } else if (admin?.assignedStation) {
      setSelectedStation(admin.assignedStation._id);
    }
  }, [admin]);

  useEffect(() => {
    if (selectedStation) {
      fetchDashboardData();
    } else if (admin?.role === 'super_admin' && !selectedStation) {
      setLoading(false);
    }
  }, [selectedStation]);

  // Auto-refresh dashboard every 30 seconds
  useEffect(() => {
    if (!selectedStation) return;

    const refreshInterval = setInterval(() => {
      // Silent refresh without showing loading spinner
      const fetchSilently = async () => {
        try {
          const [dashboardRes, bookingsRes] = await Promise.all([
            adminsAPI.getDashboard({ station: selectedStation }),
            bookingsAPI.getRequests({ station: selectedStation, status: 'pending' })
          ]);

          setStats(dashboardRes.data.stats);
          setRecentBookings(bookingsRes.data.bookings.slice(0, 5));
        } catch (error) {
          console.error('Error refreshing dashboard:', error);
        }
      };

      fetchSilently();
    }, 30000); // 30 seconds

    return () => clearInterval(refreshInterval);
  }, [selectedStation]);

  const fetchStations = async () => {
    try {
      const response = await stationsAPI.getAll();
      setStations(response.data.stations);
      if (response.data.stations.length > 0) {
        setSelectedStation(response.data.stations[0]._id);
      }
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, bookingsRes, stationRes] = await Promise.all([
        adminsAPI.getDashboard({ station: selectedStation }),
        bookingsAPI.getRequests({ station: selectedStation, status: 'pending' }),
        stationsAPI.getOne(selectedStation)
      ]);

      setStats(dashboardRes.data.stats);
      setRecentBookings(bookingsRes.data.bookings.slice(0, 5));
      setStation(stationRes.data.station);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'badge-warning',
      approved: 'badge-success',
      checked_in: 'badge-info',
      checked_out: 'badge-neutral',
      rejected: 'badge-danger'
    };
    return styles[status] || 'badge-neutral';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-midnight-900">
              Station Dashboard
            </h1>
            {station && (
              <p className="text-midnight-600 mt-1">
                {station.name} ({station.code})
              </p>
            )}
          </div>

          {/* Station Selector for Super Admin */}
          {admin?.role === 'super_admin' && (
            <select
              value={selectedStation}
              onChange={(e) => setSelectedStation(e.target.value)}
              className="input-field max-w-xs"
            >
              {stations.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name} ({s.code})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Rooms"
            value={stats?.totalRooms || 0}
            icon={BedDouble}
            color="midnight"
          />
          <StatCard
            title="Available Rooms"
            value={stats?.availableRooms || 0}
            icon={CheckCircle}
            color="green"
          />
          <StatCard
            title="Occupied Rooms"
            value={stats?.occupiedRooms || 0}
            icon={Users}
            color="blue"
          />
          <StatCard
            title="Pending Requests"
            value={stats?.pendingRequests || 0}
            icon={ClipboardList}
            color="orange"
          />
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-midnight-500 text-sm">Under Maintenance</p>
                <p className="text-2xl font-bold text-midnight-900 mt-1">
                  {stats?.maintenanceRooms || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-midnight-500 text-sm">Today's Check-ins</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {stats?.todayCheckIns || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <LogIn className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-midnight-500 text-sm">Today's Check-outs</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {stats?.todayCheckOuts || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <LogOut className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Room Availability Chart */}
        <div className="card">
          <div className="p-6 border-b border-midnight-100">
            <h2 className="text-lg font-display font-semibold text-midnight-900">
              Room Availability Overview
            </h2>
          </div>
          <div className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span className="text-sm text-midnight-600">Available ({stats?.availableRooms || 0})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-sm text-midnight-600">Occupied ({stats?.occupiedRooms || 0})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span className="text-sm text-midnight-600">Maintenance ({stats?.maintenanceRooms || 0})</span>
              </div>
            </div>

            {/* Simple Progress Bar */}
            <div className="h-8 bg-midnight-100 rounded-full overflow-hidden flex">
              <div
                className="bg-green-500 transition-all duration-500"
                style={{ width: `${(stats?.availableRooms / stats?.totalRooms) * 100 || 0}%` }}
              ></div>
              <div
                className="bg-red-500 transition-all duration-500"
                style={{ width: `${(stats?.occupiedRooms / stats?.totalRooms) * 100 || 0}%` }}
              ></div>
              <div
                className="bg-yellow-500 transition-all duration-500"
                style={{ width: `${(stats?.maintenanceRooms / stats?.totalRooms) * 100 || 0}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Pending Requests */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-display font-semibold">Pending Booking Requests</h2>
            <div className="flex items-center gap-3">
              <Link
                to="/admin/schedule"
                className="text-sm text-white/70 hover:text-white"
              >
                Schedule
              </Link>
              <Link
                to="/admin/requests"
                className="text-sm text-white/80 hover:text-white flex items-center gap-1"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="p-6">
            {recentBookings.length === 0 ? (
              <p className="text-midnight-500 text-center py-8">No pending requests</p>
            ) : (
              <div className="space-y-4">
                {recentBookings.map((booking) => (
                  <div
                    key={booking._id}
                    className="flex items-center justify-between p-4 bg-midnight-50 rounded-xl"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-railway-100 rounded-full flex items-center justify-center">
                        <span className="font-bold text-railway-600">
                          {booking.guestName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-midnight-900">{booking.guestName}</p>
                        <p className="text-sm text-midnight-500">
                          {booking.requestedCategory?.name || 'Any Category'} • {booking.countryCode || '+91'} {booking.mobile}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={getStatusBadge(booking.status)}>
                        {booking.status}
                      </span>
                      <Link
                        to="/admin/requests"
                        className="text-railway-500 hover:text-railway-600"
                      >
                        <ArrowRight className="w-5 h-5" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
